import React, { PropsWithChildren } from 'react';

import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { OnboardingPage } from '@/layout/OnboardingPage';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useAuth, useUser } from '@/lib/auth';
import { Logger } from '@/lib/logs/logger';
import { useMenuItems } from '@/pages/shared/hooks/useMenuItems';

import { ActivateAccountFailed, ActivateAccountLoading, ActivateAccountSuccess } from './components';
import { BaseActivateAccountPageState, useActivateAccount } from './hooks/useActivateAccount';

export const ActivateAccountPage: React.FC = () => {
  const { isSignedIn } = useAuth();

  if (isSignedIn) {
    return (
      <RoleSpecificPageLayout>
        <PageContent />
      </RoleSpecificPageLayout>
    );
  }

  return (
    <OnboardingPage>
      <PageContent />
    </OnboardingPage>
  );
};

const RoleSpecificPageLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const menuItems = useMenuItems();

  const userType = user.membership.type;
  if ([MembershipWithOrganizationTypeEnum.land_steward, MembershipWithOrganizationTypeEnum.buyer].includes(userType)) {
    return (
      <SignedInPageLayout data-testid='page-layout' items={menuItems}>
        {children}
      </SignedInPageLayout>
    );
  }

  Logger.errorOnce(`Unknown membership type "${user.membership.type}"`);
  return <OnboardingPage>{children}</OnboardingPage>;
};

const PageContent: React.FC = () => {
  const { pageState } = useActivateAccount();

  const Content = () => {
    switch (pageState) {
      case BaseActivateAccountPageState.LOADING:
        return <ActivateAccountLoading />;
      case BaseActivateAccountPageState.SUCCESS:
        return <ActivateAccountSuccess />;
      default:
        return <ActivateAccountFailed pageState={pageState} />;
    }
  };

  return (
    <div className='m-auto max-w-[526px]'>
      <Content />
    </div>
  );
};
